import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, Link, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft,
  Download,
  FileText,
  Calendar,
  Building,
  PenTool,
  Loader2,
  CheckCircle,
  Clock,
  XCircle,
  AlertCircle,
  Send,
  X,
  LucideIcon,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import api from '../services/api'
import type { Proposal } from '../types'
import { formatCurrency, formatDate } from '../utils/helpers'
import LineItemsTable from '../components/LineItemsTable'

const STATUS_CONFIG: Record<number, { label: string; color: string; icon: LucideIcon }> = {
  [-1]: { label: 'Annulé', color: 'bg-gray-100 text-gray-800', icon: XCircle },
  0: { label: 'Brouillon', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  1: { label: 'Validé', color: 'bg-blue-100 text-blue-800', icon: AlertCircle },
  2: { label: 'Signé', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  3: { label: 'Non signé', color: 'bg-red-100 text-red-800', icon: XCircle },
}

export default function ProposalDetail() {
  const { t } = useTranslation()
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const erpId = searchParams.get('erp_id') ? Number(searchParams.get('erp_id')) : undefined
  const [proposal, setProposal] = useState<Proposal | null>(null)
  const [loading, setLoading] = useState(true)
  const [downloading, setDownloading] = useState(false)
  const [signing, setSigning] = useState(false)
  const [sendingForSig, setSendingForSig] = useState(false)
  const [polling, setPolling] = useState(false)
  const [signedBanner, setSignedBanner] = useState(false)
  const [signingUrl, setSigningUrl] = useState<string | null>(null)
  const pollIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // Detect admin role for the "Send for signature" button
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}')
  const isAdmin = currentUser.role === 'admin' || currentUser.role === 'super_admin'
  const isImpersonating = !!api.getImpersonatedCustomer()

  const loadProposal = useCallback(async () => {
    if (!id) return
    try {
      const data = await api.getProposal(id, erpId)
      setProposal(data)
    } catch (error: unknown) {
      console.error('Error loading proposal:', error)
    } finally {
      setLoading(false)
    }
  }, [id, erpId])

  useEffect(() => {
    loadProposal()
  }, [loadProposal])

  useEffect(
    () => () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current)
      if (pollTimeoutRef.current) clearTimeout(pollTimeoutRef.current)
    },
    []
  )

  const handleDownloadPDF = async () => {
    if (!id || !proposal) return
    setDownloading(true)
    try {
      const isSigned = proposal.signature_status === 'signed' || Number(proposal.status) === 2
      const pdfBase64 = isSigned
        ? await api.downloadProposalSignedPDF(id, erpId)
        : await api.downloadProposalPDF(id, erpId)
      if (pdfBase64) {
        const link = document.createElement('a')
        link.href = `data:application/pdf;base64,${pdfBase64}`
        link.download = `${proposal.ref}.pdf`
        link.click()
      }
    } catch (error) {
      console.error('Error downloading PDF:', error)
    } finally {
      setDownloading(false)
    }
  }

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current)
      pollIntervalRef.current = null
    }
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }
    setPolling(false)
  }

  const startPolling = (proposalId: string) => {
    stopPolling()
    setPolling(true)

    const poll = async () => {
      try {
        const result = await api.getProposalSignStatus(proposalId)
        if (result.status === 'signed') {
          stopPolling()
          setSignedBanner(true)
          await loadProposal()
        }
      } catch {
        // silent
      }
    }

    pollIntervalRef.current = setInterval(poll, 5000)
    pollTimeoutRef.current = setTimeout(stopPolling, 10 * 60 * 1000)
  }

  const handleSign = async () => {
    if (!id || !proposal) return
    if (!confirm(`Voulez-vous initier la signature électronique du devis ${proposal.ref} ?`)) return
    setSigning(true)
    try {
      const result = await api.signProposal(id)
      if (result.signing_url) setSigningUrl(result.signing_url)
      await loadProposal()
      startPolling(id)
    } catch (error) {
      console.error('Error signing proposal:', error)
      alert('Erreur lors de la demande de signature. Veuillez réessayer.')
    } finally {
      setSigning(false)
    }
  }

  const handleAdminSendForSignature = async () => {
    if (!id || !proposal) return
    if (!confirm(`Envoyer le devis ${proposal.ref} pour signature électronique au client ?`)) return
    setSendingForSig(true)
    try {
      await api.adminSendProposalForSignature(id)
      alert(t('proposalDetail.sendForSignatureSuccess', { ref: proposal.ref }))
    } catch (error: unknown) {
      const apiErr = error as { response?: { data?: { error?: string } } }
      console.error('Error sending proposal for signature:', apiErr)
      const msg = apiErr || "Erreur lors de l'envoi pour signature."
      alert(msg)
    } finally {
      setSendingForSig(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!proposal) {
    return (
      <div className="text-center py-12">
        <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('proposalDetail.notFound')}</h2>
        <Link to="/proposals" className="text-primary-600 hover:text-primary-700">
          {t('proposalDetail.backToList')}
        </Link>
      </div>
    )
  }

  const statusNum =
    typeof proposal.status === 'string' ? parseInt(proposal.status, 10) : Number(proposal.status)
  const statusCfg = STATUS_CONFIG[statusNum] ?? {
    label: 'Inconnu',
    color: 'bg-gray-100 text-gray-800',
    icon: Clock,
  }
  const StatusIcon = statusCfg.icon
  const canSign = statusNum === 1

  return (
    <div className="space-y-6 animate-fade-in">
      {/* "Document signed" banner */}
      {signedBanner && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-green-800 font-semibold">
              {t('proposalDetail.signedTitle', { ref: proposal.ref })}
            </p>
            <p className="text-green-700 text-sm mt-1">{t('proposalDetail.signedBody')}</p>
          </div>
          <button
            onClick={() => setSignedBanner(false)}
            className="text-green-500 hover:text-green-700 flex-shrink-0"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Polling indicator */}
      {polling && !signedBanner && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center gap-3">
          <Loader2 className="h-5 w-5 text-blue-600 flex-shrink-0 animate-spin" />
          <div className="flex flex-col gap-1">
            <p className="text-blue-800 text-sm">
              {t('proposalDetail.signPolling', { ref: proposal.ref })}
            </p>
            {signingUrl && (
              <a
                href={signingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 text-sm font-medium underline"
              >
                {t('proposals.signNow')}
              </a>
            )}
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center space-x-4">
          <Link to="/proposals" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {t('proposalDetail.title', { ref: proposal.ref })}
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              {t('proposalDetail.createdOn', { date: formatDate(proposal.date) })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Send invitation: admin, or any user viewing a client context (impersonation) */}
          {(isAdmin || isImpersonating) && canSign && (
            <button
              onClick={handleAdminSendForSignature}
              disabled={sendingForSig}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-colors disabled:opacity-50"
            >
              {sendingForSig ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
              {sendingForSig
                ? t('proposalDetail.sendingForSignature')
                : t('proposalDetail.sendForSignature')}
            </button>
          )}
          {/* Sign: regular customer only (not admin, not impersonating a client) */}
          {canSign && !isAdmin && !isImpersonating && (
            <button
              onClick={handleSign}
              disabled={signing}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-lg transition-colors disabled:opacity-50"
            >
              {signing ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <PenTool className="w-4 h-4" />
              )}
              {signing ? t('proposalDetail.signing') : t('proposalDetail.sign')}
            </button>
          )}
          <button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            {downloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            {downloading ? t('proposalDetail.downloading') : t('proposalDetail.downloadPdf')}
          </button>
        </div>
      </div>

      {/* Status badge */}
      <div>
        <span
          className={`inline-flex items-center gap-1.5 px-3 py-1 text-sm font-medium rounded-full ${statusCfg.color}`}
        >
          <StatusIcon className="w-4 h-4" />
          {proposal.status_label || statusCfg.label}
        </span>
      </div>

      {/* General info */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">{t('proposalDetail.generalInfo')}</h2>
        </div>
        <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Customer name + address */}
          {(proposal.customer_name || proposal.customer_address) && (
            <div className="flex items-start space-x-3 md:col-span-2">
              <div className="w-10 h-10 bg-indigo-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Building className="w-5 h-5 text-indigo-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('proposalDetail.customer')}</p>
                {proposal.customer_name && (
                  <p className="font-medium text-gray-900">{proposal.customer_name}</p>
                )}
                {proposal.customer_address && (
                  <p className="text-sm text-gray-600">{proposal.customer_address}</p>
                )}
                {(proposal.customer_zip || proposal.customer_city) && (
                  <p className="text-sm text-gray-600">
                    {[proposal.customer_zip, proposal.customer_city].filter(Boolean).join(' ')}
                  </p>
                )}
                {proposal.customer_country && (
                  <p className="text-sm text-gray-500">{proposal.customer_country}</p>
                )}
              </div>
            </div>
          )}

          <div className="flex items-start space-x-3">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <Calendar className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-500">{t('proposalDetail.proposalDate')}</p>
              <p className="font-medium text-gray-900">{formatDate(proposal.date)}</p>
            </div>
          </div>

          {proposal.end_validity_date && (
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Calendar className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('proposalDetail.validityDate')}</p>
                <p className="font-medium text-gray-900">
                  {formatDate(proposal.end_validity_date)}
                </p>
              </div>
            </div>
          )}

          {proposal.date_creation && (
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <FileText className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('proposalDetail.creationDate')}</p>
                <p className="font-medium text-gray-900">{formatDate(proposal.date_creation)}</p>
              </div>
            </div>
          )}

          {proposal.customer_ref && (
            <div className="flex items-start space-x-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <Building className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">{t('proposalDetail.customerRef')}</p>
                <p className="font-medium text-gray-900">{proposal.customer_ref}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Lines */}
      {proposal.lines !== undefined && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">{t('lineItems.title')}</h2>
          </div>
          <div className="p-6">
            <LineItemsTable lines={proposal.lines ?? []} />
          </div>
        </div>
      )}

      {/* Amounts */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="p-6 border-b border-gray-100">
          <h2 className="text-lg font-semibold text-gray-900">{t('proposalDetail.amounts')}</h2>
        </div>
        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <span className="text-gray-600">{t('proposalDetail.totalExclTax')}</span>
            <span className="font-medium text-gray-900">
              {formatCurrency(proposal.total_excl_tax)}
            </span>
          </div>
          <div className="flex items-center justify-between py-3 border-b border-gray-100">
            <span className="text-gray-600">{t('proposalDetail.vat')}</span>
            <span className="font-medium text-gray-900">{formatCurrency(proposal.vat_amount)}</span>
          </div>
          <div className="flex items-center justify-between py-3">
            <span className="text-lg font-semibold text-gray-900">
              {t('proposalDetail.totalInclTax')}
            </span>
            <span className="text-lg font-bold text-gray-900">
              {formatCurrency(proposal.total_incl_tax)}
            </span>
          </div>
        </div>
      </div>

      {/* Notes */}
      {proposal.notes && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">{t('proposalDetail.notes')}</h2>
          </div>
          <div className="p-6">
            <p className="text-gray-700 whitespace-pre-wrap">{proposal.notes}</p>
          </div>
        </div>
      )}
    </div>
  )
}
